/**
 * CR456: Tab 3 — Validation Report (system-wide)
 */
import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, Copy, ClipboardList } from "lucide-react";
import { useFlyerSources } from "./useFlyerSources";
import { validateMapping, generateFixSuggestions } from "@/lib/imageCapture/validateMapping";
import { useToast } from "@/hooks/use-toast";

type HealthLevel = "OK" | "WARN" | "ERROR";

function HealthBadge({ health }: { health: HealthLevel }) {
  const variants: Record<HealthLevel, string> = {
    OK: "bg-primary text-primary-foreground",
    WARN: "bg-secondary text-secondary-foreground border border-border",
    ERROR: "bg-destructive text-destructive-foreground",
  };
  const labels: Record<HealthLevel, string> = { OK: "✓ OK", WARN: "⚠ WARN", ERROR: "✗ ERROR" };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variants[health]}`}>
      {labels[health]}
    </span>
  );
}

export function ValidationTab() {
  const { sources, loading, fetchSources } = useFlyerSources();
  const { toast } = useToast();
  const [expanded, setExpanded] = useState<string | null>(null);

  const results = sources.map((source) => ({
    source,
    result: validateMapping(source),
    fixes: generateFixSuggestions(source, validateMapping(source)),
  }));

  const okCount = results.filter((r) => r.result.health === "OK").length;
  const warnCount = results.filter((r) => r.result.health === "WARN").length;
  const errorCount = results.filter((r) => r.result.health === "ERROR").length;
  const eligibleCount = results.filter(
    (r) => r.result.health === "OK" && r.source.flyer_image_capture_enabled
  ).length;

  const allFixes = [...new Set(results.flatMap((r) => r.fixes))];

  const copyFixes = () => {
    if (allFixes.length === 0) {
      toast({ title: "No fix suggestions" });
      return;
    }
    navigator.clipboard.writeText(allFixes.join("\n"));
    toast({ title: "Fix suggestions copied" });
  };

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">Total Sources</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{results.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">Eligible (OK)</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{eligibleCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">Warnings</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{warnCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1 pt-4 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground">Errors</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="text-2xl font-bold">{errorCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={fetchSources} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
        {allFixes.length > 0 && (
          <Button variant="outline" size="sm" onClick={copyFixes}>
            <Copy className="h-4 w-4 mr-1" />
            Copy Fix SQL ({allFixes.length})
          </Button>
        )}
      </div>

      {/* Results table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Merchant</TableHead>
              <TableHead>Adapter</TableHead>
              <TableHead className="w-[100px]">Health</TableHead>
              <TableHead>Issues</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : results.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No FLYER sources found.</TableCell></TableRow>
            ) : (
              results.map(({ source, result, fixes }) => (
                <Fragment key={source.merchant_source_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => setExpanded(expanded === source.merchant_source_id ? null : source.merchant_source_id)}
                  >
                    <TableCell>
                      <div className="font-medium">{source.merchant_name || source.merchant_id}</div>
                      <div className="text-xs text-muted-foreground font-mono">{source.merchant_source_id.slice(0, 8)}…</div>
                    </TableCell>
                    <TableCell>
                      {source.adapter_display_name ? (
                        <div>
                          <div className="text-sm">{source.adapter_display_name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{source.flyer_image_capture_adapter_key}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><HealthBadge health={result.health} /></TableCell>
                    <TableCell>
                      {result.issues.length === 0
                        ? <span className="text-xs text-muted-foreground">—</span>
                        : <span className="text-xs text-muted-foreground">{result.issues.length} issue{result.issues.length > 1 ? "s" : ""}</span>
                      }
                    </TableCell>
                  </TableRow>

                  {expanded === source.merchant_source_id && (result.issues.length > 0 || fixes.length > 0) && (
                    <TableRow className="bg-muted/20">
                      <TableCell colSpan={4} className="py-3 px-6">
                        {result.issues.length > 0 && (
                          <div className="space-y-1 mb-3">
                            {result.issues.map((iss, i) => (
                              <div key={`${source.merchant_source_id}-${iss.code}-${i}`} className={`flex gap-2 text-xs ${iss.level === "ERROR" ? "text-destructive" : "text-muted-foreground"}`}>
                                <span>{iss.level === "ERROR" ? "✗" : "⚠"}</span>
                                <span><strong>[{iss.code}]</strong> {iss.message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {fixes.length > 0 && (
                          <div>
                            <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                              <ClipboardList className="h-3 w-3" />
                              Fix suggestions:
                            </div>
                            <pre className="text-xs bg-background rounded p-2 border font-mono overflow-x-auto">
                              {fixes.join("\n")}
                            </pre>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
